import Pagination from "@/components/Pagination";
import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Design from "@/redux/models/Design";
import Profile from "@/redux/models/Profile";
import { TrashIcon } from "@heroicons/react/outline";
import DesignNameForm from "@/components/DesignNameForm";
import Checkout from "@/redux/models/Checkout";
import { useRouter } from "next/navigation";
import { DesignListGA } from "@/utils/googleTag";
import { useSession } from "next-auth/react";
import { handleNotAllowedError } from "@/utils/error";
import LoadingSpinner from "../LoadingSpinner";
import { ConfirmationModal } from "../ConfirmationModal";
import { ReorderButton } from "./ReorderButton";
import ItemDesignDisplay from "../ItemDesignDisplay/ItemDesignDisplay";

const perPage = 10;

const Designs = () => {
  const designs = useSelector(Profile.selectors.getDesignsList);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [designId, setDesignId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const dispatch = useDispatch();
  const customShape = useSelector(Checkout.selectors.getCustomShape);
  const router = useRouter();
  const { status } = useSession();
  const isLogged = status === "authenticated";

  const totalPages = Math.ceil(designs.length / perPage);

  const handleDeleteDesign = (designId) => {
    setShowConfirmationModal(true);
    setDesignId(designId);
  };

  const handleConfirmDeleteDesign = useCallback(() => {
    dispatch(Design.types.deleteDesign({ id: designId }));
    setShowConfirmationModal(false);
    setDesignId(null);
  }, [designId]);

  useEffect(() => {
    dispatch(
      Profile.types.getDesigns({
        finallyCb: () => setLoading(false),
        catchCb: (err) => handleNotAllowedError(err, router, isLogged),
      })
    );
    if (!customShape?.id) {
      dispatch(Checkout.types.getShapes());
      dispatch(Checkout.types.getScents());
      dispatch(Checkout.types.getStrings());
    }
    setPage(1);
  }, []);

  useEffect(() => DesignListGA(), []);

  if (!loading && !designs?.length) return <div className="w-full relative">No Designs found</div>;

  return (
    <div className="w-full relative">
      {loading && (
        <div className="absolute z-50 flex w-full justify-center text-center h-full pt-16 bg-opacity-40 bg-slate-50">
          <LoadingSpinner className="text-blue-500 h-10 w-10" />
        </div>
      )}
      <div className="w-full">
        <section>
          <div className="hidden lg:grid lg:grid-cols-[2fr_2fr_2fr_2fr_1fr_2fr] w-full text-xs capitalize text-gray-900 font-semibold underline">
            <div className="flex w-full justify-center items-center text-center"></div>
            <div className="flex w-full justify-center items-center text-center">Name</div>
            <div className="flex w-full justify-center items-center text-center">Shape</div>
            <div className="flex w-full justify-center items-center text-center">Created</div>
            <div className="flex w-full justify-center items-center text-center"></div>
            <div className="flex w-full justify-center items-center text-center"></div>
          </div>
        </section>
        <section className="w-full px-4">
          {designs?.slice((page - 1) * perPage, (page - 1) * perPage + perPage)?.map((design) => (
            <div
              key={design.id}
              className="grid grid-cols-2 lg:grid-cols-[2fr_2fr_2fr_2fr_1fr_2fr] w-full text-xs capitalize py-4 border-b"
            >
              <div className="flex w-full justify-center items-center text-center col-span-2 lg:col-span-1 lg:mb-0 mb-5">
                <ItemDesignDisplay
                  item={{
                    design,
                    hcard_design: design.hcard_design,
                  }}
                />
              </div>
              <div className="flex w-full lg:justify-center items-center text-center col-span-2 lg:col-span-1">
                <span className="block lg:hidden font-bold mr-2">Name:</span>
                <DesignNameForm
                  defaultName={design.name}
                  designId={design.id}
                  inputClass="lg:text-center py-1.5 px-2 md:w-[150px] w-full placeholder:text-gray-500 text-sm rounded-md outline-none mr-[5px]"
                />
              </div>
              <div className="flex w-full lg:justify-center items-center text-center col-span-2 lg:col-span-1">
                <span className="block lg:hidden font-bold mr-2">Shape:</span>
                {design.shape.name}
              </div>
              <div className="flex w-full lg:justify-center items-center text-center col-span-2 lg:col-span-1 lg:mt-0 mt-2">
                <span className="block lg:hidden font-bold mr-2">Created:</span>
                {design.created_date ? dayjs(design.created_date).format("MM/DD/YYYY") : "-"}
              </div>

              <div className="flex w-full justify-center items-center text-center"></div>

              <div className="flex w-full justify-center items-center text-center col-span-2 lg:col-span-1 lg:mt-0 mt-5">
                <div className="flex justify-center space-x-2">
                  <ReorderButton design={design} />
                  <div>
                    <button
                      type="button"
                      className="red-button"
                      onClick={() => handleDeleteDesign(design.id)}
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}

          <ConfirmationModal
            isOpened={showConfirmationModal}
            onCancel={() => {
              setShowConfirmationModal(false);
              setDesignId(null);
            }}
            onConfirm={handleConfirmDeleteDesign}
            text="Are you sure? This action cannot be undone."
          />
        </section>

        <section className="w-full py-2 flex items-center justify-center">
          {totalPages > 1 && (
            <Pagination
              page={page}
              total={totalPages}
              disableNext={page === totalPages}
              disablePrev={page === 1}
              handleNext={() => setPage(page + 1)}
              handlePrev={() => setPage(page - 1)}
            />
          )}
        </section>
      </div>
    </div>
  );
};

export default Designs;
